import React, { useMemo } from 'react';
import Paper from '@mui/material/Paper';
import { getFormattedTableColumns } from 'components/ChartContainer/components/Chart/graphs/TableChart/defaultSettings';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import {
    AGGRID_DEFAULT_COL_DEF,
    AGGRID_DEFAULT_HEADER_HEIGHT,
} from 'ui/AgGridReact/constants';
import TextFloatingFilter from 'ui/AgGridReact/TextFloatingFilter';
import NumberFloatingFilter from 'ui/AgGridReact/NumberFloatingFilter';
import { styled } from '@mui/material';
import AgGridReact from 'ui/AgGridReact/AgGridReact';

const StyCell = styled('button', {
    name: 'StyCell',
})({
    width: '100%',
    height: '100%',
    backgroundColor: 'transparent',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
});

const getCellRenderer = (params: ICellRendererParams) => {
    return <StyCell type="button">{params.valueFormatted}</StyCell>;
};

type Props = {
    backendData: GetDataResponse;
};

const StyPaper = styled(Paper, {
    name: 'StyPaper',
})({
    '& .ag-root-wrapper-body': {
        height: 'auto !important' as 'auto',
    },
    '& .ag-tool-panel-wrapper': {
        width: '100%',
    },
    '&&& .ag-floating-filter .MuiTextField-root': {
        alignItems: 'center',
    },
    '&&& .ag-floating-filter-body': {
        alignItems: 'center',
    },
    '&&& .ag-input-field-input': {
        height: '100%',
    },
});

const LoadedTable = ({ backendData }: Props): React.ReactElement => {
    const defaultColDef: ColDef = {
        ...AGGRID_DEFAULT_COL_DEF,
        flex: 1,
        lockVisible: false,
        cellRenderer: getCellRenderer,
        comparator: (valueA, valueB) => {
            if (valueA === valueB) return 0;
            const comp = valueA > valueB ? 1 : -1;
            return comp;
        },
    };

    const colDefs = useMemo(() => {
        return getFormattedTableColumns(backendData.columns, false);
    }, [backendData.columns]);

    return (
        backendData.data && (
            <StyPaper>
                <AgGridReact
                    className="ag-theme-material"
                    suppressColumnVirtualisation
                    rowData={backendData.data}
                    columnDefs={colDefs}
                    defaultColDef={defaultColDef}
                    headerHeight={AGGRID_DEFAULT_HEADER_HEIGHT}
                    components={{
                        textFloatingFilter: TextFloatingFilter,
                        numberFloatingFilter: NumberFloatingFilter,
                    }}
                    pagination={true}
                    paginationPageSize={15}
                    rowHeight={20}
                    alwaysMultiSort={true}
                    suppressDragLeaveHidesColumns={true}
                />
            </StyPaper>
        )
    );
};

export default LoadedTable;
